import { z } from 'zod'
import { BaseCsvExportRequest, baseCsvExportRequestSchema } from './CsvExportTypes'

export const ACCOUNTS_CSV_EXPORT_FIELD_IDS = [
  'accountGuid',
  'accountDisplayName',
  'accountType',
  'archived',
  'createdAt',
  'accountTags',
  'accountManagerName',
  'primaryContactName',
  'primaryContactPhone',
  'primaryContactEmail',
  'doNotServiceFlag',
  'doNotServiceReason',
  'locations',
  'businessAddress',
  'totalLocationsCount',
  'equipmentCount',
  'equipmentTypes',
  'equipmentModelNumbers',
  'equipmentSerialNumbers',
  'totalJobsCount',
  'lastJobDate',
  'daysSinceLastJob',
  'lifetimeValueUsd',
  'outstandingBalanceUsd',
  'totalPaidUsd',
  'openInvoicesCount',
  'overdueInvoicesCount',
  'averageInvoiceAmountUsd',
  'lastInvoiceDate',
  'lastPaymentDate',
  'activeMaintenancePlansCount',
  'maintenancePlanTypes',
  'isMembershipCustomer',
  'customerTenureMonths',
] as const

export type AccountsCsvExportFieldId = (typeof ACCOUNTS_CSV_EXPORT_FIELD_IDS)[number]

export type CsvExportFieldGroupId =
  | 'ACCOUNT'
  | 'CONTACT'
  | 'LOCATIONS'
  | 'EQUIPMENT'
  | 'MAINTENANCE_PLANS'
  | 'FINANCIAL'
  | 'FLAGS'

export type CsvExportFieldDefinition<TFieldId extends string> = {
  id: TFieldId
  label: string
  description?: string
  groupId: CsvExportFieldGroupId
  defaultSelected: boolean
}

export const accountsCsvExportFieldDefinitions: Record<
  AccountsCsvExportFieldId,
  CsvExportFieldDefinition<AccountsCsvExportFieldId>
> = {
  accountGuid: { id: 'accountGuid', label: 'Account GUID', groupId: 'ACCOUNT', defaultSelected: false },
  accountDisplayName: {
    id: 'accountDisplayName',
    label: 'Account Display Name',
    groupId: 'ACCOUNT',
    defaultSelected: true,
  },
  accountType: { id: 'accountType', label: 'Account Type', groupId: 'ACCOUNT', defaultSelected: true },
  archived: { id: 'archived', label: 'Archived', groupId: 'ACCOUNT', defaultSelected: false },
  createdAt: { id: 'createdAt', label: 'Created At', groupId: 'ACCOUNT', defaultSelected: false },
  accountTags: { id: 'accountTags', label: 'Account Tags', groupId: 'ACCOUNT', defaultSelected: false },
  accountManagerName: {
    id: 'accountManagerName',
    label: 'Account Manager Name',
    groupId: 'ACCOUNT',
    defaultSelected: false,
  },
  primaryContactName: {
    id: 'primaryContactName',
    label: 'Primary Contact Name',
    groupId: 'CONTACT',
    defaultSelected: true,
  },
  primaryContactPhone: {
    id: 'primaryContactPhone',
    label: 'Primary Contact Phone',
    groupId: 'CONTACT',
    defaultSelected: true,
  },
  primaryContactEmail: {
    id: 'primaryContactEmail',
    label: 'Primary Contact Email',
    groupId: 'CONTACT',
    defaultSelected: true,
  },
  doNotServiceFlag: { id: 'doNotServiceFlag', label: 'Do Not Service Flag', groupId: 'FLAGS', defaultSelected: false },
  doNotServiceReason: {
    id: 'doNotServiceReason',
    label: 'Do Not Service Reason',
    groupId: 'FLAGS',
    defaultSelected: false,
  },
  locations: { id: 'locations', label: 'Locations', groupId: 'LOCATIONS', defaultSelected: true },
  businessAddress: { id: 'businessAddress', label: 'Business Address', groupId: 'LOCATIONS', defaultSelected: false },
  totalLocationsCount: {
    id: 'totalLocationsCount',
    label: 'Total Locations Count',
    groupId: 'LOCATIONS',
    defaultSelected: false,
  },
  equipmentCount: { id: 'equipmentCount', label: 'Equipment Count', groupId: 'EQUIPMENT', defaultSelected: true },
  equipmentTypes: { id: 'equipmentTypes', label: 'Equipment Types', groupId: 'EQUIPMENT', defaultSelected: false },
  equipmentModelNumbers: {
    id: 'equipmentModelNumbers',
    label: 'Equipment Model Numbers',
    groupId: 'EQUIPMENT',
    defaultSelected: false,
  },
  equipmentSerialNumbers: {
    id: 'equipmentSerialNumbers',
    label: 'Equipment Serial Numbers',
    groupId: 'EQUIPMENT',
    defaultSelected: false,
  },
  totalJobsCount: { id: 'totalJobsCount', label: 'Total Jobs Count', groupId: 'FINANCIAL', defaultSelected: true },
  lastJobDate: { id: 'lastJobDate', label: 'Last Job Date', groupId: 'FINANCIAL', defaultSelected: true },
  daysSinceLastJob: {
    id: 'daysSinceLastJob',
    label: 'Days Since Last Job',
    groupId: 'FINANCIAL',
    defaultSelected: false,
  },
  lifetimeValueUsd: {
    id: 'lifetimeValueUsd',
    label: 'Lifetime Value (USD)',
    groupId: 'FINANCIAL',
    defaultSelected: true,
  },
  outstandingBalanceUsd: {
    id: 'outstandingBalanceUsd',
    label: 'Outstanding Balance (USD)',
    groupId: 'FINANCIAL',
    defaultSelected: true,
  },
  totalPaidUsd: { id: 'totalPaidUsd', label: 'Total Paid (USD)', groupId: 'FINANCIAL', defaultSelected: false },
  openInvoicesCount: {
    id: 'openInvoicesCount',
    label: 'Open Invoices Count',
    groupId: 'FINANCIAL',
    defaultSelected: false,
  },
  overdueInvoicesCount: {
    id: 'overdueInvoicesCount',
    label: 'Overdue Invoices Count',
    groupId: 'FINANCIAL',
    defaultSelected: false,
  },
  averageInvoiceAmountUsd: {
    id: 'averageInvoiceAmountUsd',
    label: 'Average Invoice Amount (USD)',
    groupId: 'FINANCIAL',
    defaultSelected: false,
  },
  lastInvoiceDate: { id: 'lastInvoiceDate', label: 'Last Invoice Date', groupId: 'FINANCIAL', defaultSelected: false },
  lastPaymentDate: { id: 'lastPaymentDate', label: 'Last Payment Date', groupId: 'FINANCIAL', defaultSelected: false },
  activeMaintenancePlansCount: {
    id: 'activeMaintenancePlansCount',
    label: 'Active Maintenance Plans Count',
    groupId: 'MAINTENANCE_PLANS',
    defaultSelected: true,
  },
  maintenancePlanTypes: {
    id: 'maintenancePlanTypes',
    label: 'Maintenance Plan Types',
    groupId: 'MAINTENANCE_PLANS',
    defaultSelected: false,
  },
  isMembershipCustomer: {
    id: 'isMembershipCustomer',
    label: 'Is Membership Customer',
    groupId: 'MAINTENANCE_PLANS',
    defaultSelected: true,
  },
  customerTenureMonths: {
    id: 'customerTenureMonths',
    label: 'Customer Tenure (Months)',
    groupId: 'FINANCIAL',
    defaultSelected: false,
  },
}

export const accountsCsvExportFieldGroups: {
  id: CsvExportFieldGroupId
  label: string
  description?: string
  fieldIds: AccountsCsvExportFieldId[]
}[] = [
  {
    id: 'ACCOUNT',
    label: 'Account Information',
    fieldIds: [
      'accountGuid',
      'accountDisplayName',
      'accountType',
      'archived',
      'createdAt',
      'accountTags',
      'accountManagerName',
    ],
  },
  {
    id: 'CONTACT',
    label: 'Primary Contact',
    fieldIds: ['primaryContactName', 'primaryContactPhone', 'primaryContactEmail'],
  },
  {
    id: 'LOCATIONS',
    label: 'Locations',
    fieldIds: ['locations', 'businessAddress', 'totalLocationsCount'],
  },
  {
    id: 'EQUIPMENT',
    label: 'Equipment',
    fieldIds: ['equipmentCount', 'equipmentTypes', 'equipmentModelNumbers', 'equipmentSerialNumbers'],
  },
  {
    id: 'MAINTENANCE_PLANS',
    label: 'Maintenance Plans',
    fieldIds: ['activeMaintenancePlansCount', 'maintenancePlanTypes', 'isMembershipCustomer'],
  },
  {
    id: 'FINANCIAL',
    label: 'Financials & Activity',
    fieldIds: [
      'totalJobsCount',
      'lastJobDate',
      'daysSinceLastJob',
      'lifetimeValueUsd',
      'outstandingBalanceUsd',
      'totalPaidUsd',
      'openInvoicesCount',
      'overdueInvoicesCount',
      'averageInvoiceAmountUsd',
      'lastInvoiceDate',
      'lastPaymentDate',
      'customerTenureMonths',
    ],
  },
  {
    id: 'FLAGS',
    label: 'Flags',
    fieldIds: ['doNotServiceFlag', 'doNotServiceReason'],
  },
]

export const NUM_JOBS_FILTER_VALUES = ['1', '2-4', '5+'] as const
export type NumJobsFilterValue = (typeof NUM_JOBS_FILTER_VALUES)[number]

export const NUM_LOCATIONS_FILTER_VALUES = ['1', '2-4', '5+'] as const
export type NumLocationsFilterValue = (typeof NUM_LOCATIONS_FILTER_VALUES)[number]

export const LAST_JOB_DATE_FILTER_VALUES = [
  'UNDER_30_DAYS',
  '1_TO_3_MONTHS',
  '3_TO_6_MONTHS',
  '6_TO_12_MONTHS',
  '12_OR_MORE_MONTHS',
  'ANY',
] as const
export type LastJobDateFilterValue = (typeof LAST_JOB_DATE_FILTER_VALUES)[number]

export const ACCOUNT_TYPE_FILTER_VALUES = ['RESIDENTIAL', 'COMMERCIAL'] as const
export type AccountTypeFilterValue = (typeof ACCOUNT_TYPE_FILTER_VALUES)[number]

export const accountsCsvExportFiltersSchema = z.object({
  searchTerm: z.string().optional(),
  showNonMPOnly: z.boolean().default(false),
  showArchived: z.boolean().default(false),
  accountTypes: z.array(z.enum(ACCOUNT_TYPE_FILTER_VALUES)).default([]),
  tagNames: z.array(z.string()).default([]),
  numJobs: z.array(z.enum(NUM_JOBS_FILTER_VALUES)).default([]),
  numLocations: z.array(z.enum(NUM_LOCATIONS_FILTER_VALUES)).default([]),
  lastJobDate: z.enum(LAST_JOB_DATE_FILTER_VALUES).default('ANY'),
  equipmentTypes: z.array(z.string()).default([]),
  equipmentModelNumber: z.string().optional(),
  equipmentSerialNumber: z.string().optional(),
})

export type AccountsCsvExportFilters = z.infer<typeof accountsCsvExportFiltersSchema>

export const accountsCsvExportRequestSchema = baseCsvExportRequestSchema.extend({
  fieldIds: z.array(z.enum(ACCOUNTS_CSV_EXPORT_FIELD_IDS)),
  filters: accountsCsvExportFiltersSchema,
})

export type AccountsCsvExportRequest = z.infer<typeof accountsCsvExportRequestSchema>

export type AccountsCsvExportRow = {
  accountGuid: string
  accountDisplayName: string
  accountType: string
  archived: boolean
  createdAt?: string
  accountTags?: string
  accountManagerName?: string
  primaryContactName?: string
  primaryContactPhone?: string
  primaryContactEmail?: string
  doNotServiceFlag: boolean
  doNotServiceReason?: string
  locations?: string
  businessAddress?: string
  totalLocationsCount: number
  equipmentCount: number
  equipmentTypes?: string
  equipmentModelNumbers?: string
  equipmentSerialNumbers?: string
  totalJobsCount: number
  lastJobDate?: string
  daysSinceLastJob?: number
  lifetimeValueUsd: number
  outstandingBalanceUsd: number
  totalPaidUsd: number
  openInvoicesCount: number
  overdueInvoicesCount: number
  averageInvoiceAmountUsd?: number
  lastInvoiceDate?: string
  lastPaymentDate?: string
  activeMaintenancePlansCount: number
  maintenancePlanTypes?: string
  isMembershipCustomer: boolean
  customerTenureMonths?: number
}
